import React, {useEffect, useMemo, useState} from "react";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import {
    IAuthenticationType,
    IAxiosGetResponse,
    IClaimConditionNodeDto,
    IOperationType,
    IRequestPayloadType,
    IServiceConsumingType,
    IServiceDto,
    ITransactionMethod,
    ITransactionRouteDto
} from "../../interfaces/IUtilities";
import {useNavigate, useParams} from "react-router-dom";
import {create, getById, update} from "../_request";
import {useToast} from "../../modules/layout/components/ToastContext";
import {Field, Form, Formik, FormikProps} from "formik";
import FormikSelect from '../../modules/layout/components/Select/FormikSelect';
import {
    AuthenticationTypeText,
    OperationTypeText,
    RequestPayloadTypeText,
    ServiceConsumingTypeText,
    TransactionMethodText
} from "../../helpers/EnumeratorHelper";
import BackButton from "../../modules/layout/components/BackButton";
import Loader from "../../modules/layout/components/Loader";
import {AxiosResponse} from "axios";
import {useTranslation} from "react-i18next";
import * as Yup from "yup";
import ConfirmModal from "../../modules/layout/components/ConfirmModal";
import {userHasRole} from "../../../setup/auth/authService";
import { AdminWeb, Transactions_Approve } from "../../constants/constants";
import { createRootNode, getAllClaimsWithoutFilters, getProfileTypeClaim } from "../../helpers/ClaimBuilderHelper";
import { validate as uuidValidate } from "uuid";
import { useRef } from "react";

const TransactionForm: React.FC = () => {

    const {t} = useTranslation();
    const {showToast} = useToast();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [selectedName, setSelectedName] = useState<string | null>(null);
    const navigate = useNavigate();
    const {serviceid, id} = useParams();
    const isEdit = Boolean(id);
    const [service, setService] = useState({} as IServiceDto);
    const [loading, setLoading] = useState(true);
    const [canApproveTransactions, setCanApproveTransactions] = useState(false);
    const [initialValues, setInitialValues] = useState<ITransactionRouteDto>({
        id: '00000000-0000-0000-0000-000000000000',
        routeLabel: '',
        className: '',
        serviceConsumingType: IServiceConsumingType.Unknown,
        transactionMethod: ITransactionMethod.Unknown,
        authenticationType: IAuthenticationType.Unknown,
        requestPayloadType: IRequestPayloadType.Unknown,
        postLocation: '',
        accessTokenUrl: '',
        retryInterval: 0,
        enabled: false,
        spokeSubmissionOnly: false,
        operationType: IOperationType.Unknown,
        serviceId: '00000000-0000-0000-0000-000000000000',
        isNew: true,
        isDeleted: false,
        profileTypeList: [],
        isApproved: false
    });
    const validationSchema = Yup.object({
        routeLabel: Yup.string().required(t('Required')),
        className: Yup.string().required(t('Required')),
        serviceConsumingType: Yup.number()
            .notOneOf([IServiceConsumingType.Unknown],  t('PleaseSubmitAValue'))
            .required(t('Required')),
        transactionMethod: Yup.number().when("serviceConsumingType", {
            is: (val: number) => val === IServiceConsumingType.Passthrough,
            then: (schema) => schema.notOneOf([ITransactionMethod.Unknown],  t('PleaseSubmitAValue')).required(t('Required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        authenticationType: Yup.number().when("serviceConsumingType", {
            is: (val: number) => val === IServiceConsumingType.Passthrough,
            then: (schema) => schema.notOneOf([IAuthenticationType.Unknown],  t('PleaseSubmitAValue')).required(t('Required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        requestPayloadType: Yup.number().when("serviceConsumingType", {
            is: (val: number) => val === IServiceConsumingType.Passthrough,
            then: (schema) => schema.notOneOf([IRequestPayloadType.Unknown],  t('PleaseSubmitAValue')).required(t('Required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        postLocation: Yup.string().when("serviceConsumingType", {
            is: (val: number) => val === IServiceConsumingType.Passthrough,
            then: (schema) => schema.required(t('Required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        accessTokenUrl: Yup.string(),
        retryInterval: Yup.number(),
        enabled: Yup.boolean(),
        spokeSubmissionOnly: Yup.boolean(),
        operationType: Yup.number()
            .notOneOf([IOperationType.Unknown],  t('PleaseSubmitAValue'))
            .required(t('Required')),
        profileTypeList: Yup.array()
            .of(Yup.string())
            .min(1, t('PleaseSelectAtLeastOneProfileType'))
    });

    const fetchServiceDetails = async () => {
        try {
            let result = await getById("/api/services/", serviceid!);
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setService(result.data as IServiceDto);
            }
            setLoading(false);

        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        }
    }

    const fetchData = async () => {
        try {
            let result = await getById("/api/transactionroutes/", id!);
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                let data = result.data as ITransactionRouteDto;
                setInitialValues(data);

                let missingFields: string[] = [];
                if (!data.routeLabel) missingFields.push(t('RouteLabel'));
                if (!data.className) missingFields.push(t('ClassName'));
                if (!data.serviceConsumingType) missingFields.push(t('ServiceConsumingType'));
                if (!data.operationType) missingFields.push(t('OperationType'));
                if (!data.profileTypeList || data.profileTypeList.length === 0) missingFields.push(t('ProfileType'));

                if (missingFields.length > 0) 
                    setCanApproveTransactions(false);
                
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                throw new Error(t('StatusCode', {code: String(respStatus)}));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            navigate(`/services/${serviceid}/transactions/list`);
        }
    }

    useEffect(() => {
        fetchServiceDetails();
        checkUserRole();

        fetchRole();
        
        if (isEdit && id) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [showToast]);

    const handleSubmit = async (values: ITransactionRouteDto, { setErrors }: any) => {
        setLoading(true);

        let claims = await getAllClaimsWithoutFilters();
        let profileTypeClaimId = getProfileTypeClaim(claims);
        let isGuid = uuidValidate(profileTypeClaimId);
        let rootNode = {} as IClaimConditionNodeDto;
        
        if (!isGuid)
        {
            showToast(t('NotExistsProfileTypeClaimOrSync'), 'warning');
            setLoading(false);
            return;
        } 
        else
        {
            rootNode = createRootNode(values.id, values.profileTypeList, profileTypeClaimId);
        }

        let updatedValues = {...values, 
            serviceConsumingType: Number(values.serviceConsumingType),
            transactionMethod: Number(values.serviceConsumingType) === IServiceConsumingType.Passthrough ? Number(values.transactionMethod) : null,
            authenticationType: Number(values.serviceConsumingType) === IServiceConsumingType.Passthrough ? Number(values.authenticationType) : null,
            requestPayloadType: Number(values.serviceConsumingType) === IServiceConsumingType.Passthrough ? Number(values.requestPayloadType) : null,
            operationType: Number(values.operationType),
            postLocation: Number(values.serviceConsumingType) === IServiceConsumingType.Passthrough ? values.postLocation : '',
            accessTokenUrl: Number(values.serviceConsumingType) === IServiceConsumingType.Passthrough && Number(values.authenticationType) === IAuthenticationType.Jwt ? values.accessTokenUrl : '',
            retryInterval: Number(values.operationType) === IOperationType.Sync ? 0 : values.retryInterval,
            rootNode: rootNode};

        if (isEdit) {
            try {
                let result = await update('/api/transactionroutes/', updatedValues) as AxiosResponse;

                if (result.status === 200) {
                    setLoading(false);
                    showToast(t('TransactionRouteUpdatedSuccessfully'), 'success');
                    navigate(`/services/${serviceid}/transactions/list`);
                } else if (result.status === 400 && result.data?.errors) {
                    setLoading(false);

                    const formikErrors: Record<string, string> = {};
                    for (const key in result.data.errors) {
                        formikErrors[key] = t(result.data.errors[key]);

                        if (key.toLocaleLowerCase().includes("rootnode") || key.toLocaleLowerCase().includes("notexistsprofiletypeclaimorsync"))
                        {
                            showToast(t(key), key.toLocaleLowerCase().includes("rootnode") ? 'error' : 'warning');
                        }
                    }

                    setErrors(formikErrors);
                } else if (result.status === 403) {
                    navigate('/error/403');
                } else if (result.status === 401) {
                    navigate('/auth/login');
                } else if (result.status === 500) {
                    navigate('/error/500');
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }
        } else {
            try {
                let result = await create('/api/transactionroutes', {
                    ...updatedValues,
                    serviceid: serviceid,
                    isNew: !canApproveTransactions
                }) as AxiosResponse;

                if (result.status === 200) {
                    setLoading(false);
                    showToast(t('TransactionRouteCreatedSuccessfully'), 'success');
                    navigate(`/services/${serviceid}/transactions/list`);
                } else if (result.status === 400 && result.data?.errors) {
                    setLoading(false);

                    const formikErrors: Record<string, string> = {};
                    for (const key in result.data.errors) {
                        formikErrors[key] = t(result.data.errors[key]);
                        
                        if (key.toLocaleLowerCase().includes("rootnode") || key.toLocaleLowerCase().includes("notexistsprofiletypeclaimorsync"))
                            showToast(t(key), key.toLocaleLowerCase().includes("rootnode") ? 'error' : 'warning');
                        else if (key.toLocaleLowerCase().includes("forbidcreateactionfortransaction"))
                            showToast(t(key), 'error');
                    }

                    setErrors(formikErrors);
                } else if (result.status === 403) {
                    navigate('/error/403');
                } else if (result.status === 401) {
                    navigate('/auth/login');
                } else if (result.status === 500) {
                    navigate('/error/500');
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }
        }
    };

    const checkUserRole = async () => {
        setCanApproveTransactions(await userHasRole(Transactions_Approve));
    }

    // react-select option lists
    const serviceConsumingOptions = useMemo(() => [
        { value: IServiceConsumingType.RequestLib, label: ServiceConsumingTypeText[IServiceConsumingType.RequestLib] },
        { value: IServiceConsumingType.Passthrough, label: ServiceConsumingTypeText[IServiceConsumingType.Passthrough] },
    ], []);

    const transactionMethodOptions = useMemo(() => [
        { value: ITransactionMethod.GET, label: TransactionMethodText[ITransactionMethod.GET] },
        { value: ITransactionMethod.POST, label: TransactionMethodText[ITransactionMethod.POST] },
        { value: ITransactionMethod.PUT, label: TransactionMethodText[ITransactionMethod.PUT] },
        { value: ITransactionMethod.DELETE, label: TransactionMethodText[ITransactionMethod.DELETE] },
    ], []);

    const authenticationTypeOptions = useMemo(() => [
        { value: IAuthenticationType.None, label: AuthenticationTypeText[IAuthenticationType.None] },
        { value: IAuthenticationType.HandledByLib, label: AuthenticationTypeText[IAuthenticationType.HandledByLib] },
        { value: IAuthenticationType.Jwt, label: AuthenticationTypeText[IAuthenticationType.Jwt] },
        { value: IAuthenticationType.HeaderApiKey, label: AuthenticationTypeText[IAuthenticationType.HeaderApiKey] },
    ], []);

    const requestPayloadTypeOptions = useMemo(() => [
        { value: IRequestPayloadType.JSON, label: RequestPayloadTypeText[IRequestPayloadType.JSON] },
        { value: IRequestPayloadType.XML, label: RequestPayloadTypeText[IRequestPayloadType.XML] },
    ], []);

    const profileTypeOptions = useMemo(() => [
        { value: 'Individual', label: t('Individual') },
        { value: 'Organisation', label: t('Organisation') },
        { value: 'Agent', label: t('Agent') },
        { value: 'Spoke', label: t('Spoke') },
    ], [t]);

    const operationTypeOptions = useMemo(() => [
        { value: IOperationType.Sync, label: OperationTypeText[IOperationType.Sync] },
        { value: IOperationType.Async, label: OperationTypeText[IOperationType.Async] },
    ], []);

    const handleConfirmApprove = async () => {
        if (selectedId) {
            setLoading(true);
            let result = await update(`/api/transactionroutes/approve/${selectedId}`, {}) as AxiosResponse;

            if (result.status === 200) {
                setLoading(false);
                showToast(t('TransactionRouteApprovedSuccessfully'), 'success');
                await fetchData();
            } else if (result.status === 400 && result.data?.errors) {
                setLoading(false);

                const formikErrors: Record<string, string> = {};
                for (const key in result.data.errors) {
                    showToast(t(result.data.errors[key]), 'warning');
                }
            } else if (result.status === 403) {
                navigate('/error/403');
            } else if (result.status === 401) {
                navigate('/auth/login');
            } else if (result.status === 500) {
                navigate('/error/500');
            } else {
                throw new Error(t('StatusCode', {code: String(result.status)}));
            }
            setShowApproveModal(false);
            setSelectedId(null);
        }
    }

    const handleCancelApprove = () => {
        setShowApproveModal(false);
        setSelectedId(null);
    };

    const formikRef = useRef<FormikProps<ITransactionRouteDto>>(null);

    const handleApprove = async(id: string, routeLabel: string) => {   
        if (!formikRef.current) return;

        const errors = await formikRef.current.validateForm();

        if (Object.keys(errors).length > 0) {
            const formikErrors: Record<string, string> = {};
            const touchedFields: Record<string, boolean> = {};
            for (const key in errors) {
                const typedKey = key as keyof ITransactionRouteDto;
                formikErrors[key] = String(errors[typedKey]);
                touchedFields[key] = true;
            }

            formikRef.current.setErrors(formikErrors);
            formikRef.current.setTouched(touchedFields);

            return;
        }

        formikRef.current.setTouched({});
        formikRef.current.setErrors({});

        setSelectedId(id);
        setSelectedName(routeLabel);
        setShowApproveModal(true);
    };

    const fetchRole = async () => { 
        const hasRole = await userHasRole(AdminWeb);
        if (!hasRole)
            navigate("/error/403");
    };


    return (
        <div className="container mt-4 gg-container-form">


            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('Services'), href: '/services/list'},
                    {
                        title: t('BreadcrumbServiceTransationRoutes', { serviceName: service.name}),
                        href: `/services/${serviceid}/transactions/list`
                    },
                    {title: isEdit ? t('EditTransactionRoute') : t('CreateTransactionRoute')} // Active page (no href)
                ]}/>

            <h3>{isEdit ? t('EditTransactionRoute') : t('CreateTransactionRoute')}</h3>

            <Formik innerRef={formikRef} initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}
                    enableReinitialize>
                {({values, errors, touched, isSubmitting, setFieldValue}) => (
                    <Form>
                        {isSubmitting && (
                            <Loader />
                        )}

                        {/* Class Name */}
                        <div className="mb-3">
                            <label className="form-label">{t('RouteName')}</label>
                            <Field className="form-control" name="routeLabel"/>
                            {touched.routeLabel && errors.routeLabel &&
                                <div className="text-danger">{errors.routeLabel}</div>}
                        </div>
                        {/* Parameter Type */}
                        <div className="mb-3">
                            <label className="form-label">{t('ClassName')}</label>
                            <Field className="form-control" name="className" disabled={isEdit}/>
                            {touched.className && errors.className &&
                                <div className="text-danger">{errors.className}</div>}
                        </div>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="card h-100">
                                    <div className="card-body">
                                        {/* Service Consuming Type */}
                                        <div className="mb-3">
                                            <label className="form-label mb-2">{t('ServiceConsumingType')}</label>
                                            <FormikSelect
                                                name="serviceConsumingType"
                                                options={serviceConsumingOptions}
                                                isClearable
                                                clearValue={IServiceConsumingType.Unknown}
                                                isDisabled={loading}
                                                placeholder={`-- ${t('Select')} --`}
                                            />
                                            {touched.serviceConsumingType && errors.serviceConsumingType &&
                                                <div className="text-danger">{errors.serviceConsumingType}</div>}
                                        </div>
                                        {/* Transaction Method */}
                                        <div className="mb-3">
                                            <label className="form-label mb-2">{t('TransactionMethod')}</label>
                                            <FormikSelect
                                                name="transactionMethod"
                                                options={transactionMethodOptions}
                                                isClearable
                                                clearValue={ITransactionMethod.Unknown}
                                                isDisabled={Number(values.serviceConsumingType) !== IServiceConsumingType.Passthrough || loading}
                                                placeholder={`-- ${t('Select')} --`}
                                            />
                                            {touched.transactionMethod && errors.transactionMethod &&
                                                <div className="text-danger">{errors.transactionMethod}</div>}
                                        </div>
                                        {/* Authentication Type */}
                                        <div className="mb-3">
                                            <label className="form-label mb-2">{t('AuthenticationType')}</label>
                                            <FormikSelect
                                                name="authenticationType"
                                                options={authenticationTypeOptions}
                                                isClearable
                                                clearValue={IAuthenticationType.Unknown}
                                                isDisabled={Number(values.serviceConsumingType) !== IServiceConsumingType.Passthrough || loading}
                                                placeholder={`-- ${t('Select')} --`}
                                            />
                                            {touched.authenticationType && errors.authenticationType &&
                                                <div className="text-danger">{errors.authenticationType}</div>}
                                        </div>
                                        {/* Request Payload Type */}
                                        <div className="mb-3">
                                            <label className="form-label mb-2">{t('RequestPayloadType')}</label>
                                            <FormikSelect
                                                name="requestPayloadType"
                                                options={requestPayloadTypeOptions}
                                                isClearable
                                                clearValue={IRequestPayloadType.Unknown}
                                                isDisabled={Number(values.serviceConsumingType) !== IServiceConsumingType.Passthrough || loading}
                                                placeholder={`-- ${t('Select')} --`}
                                            />
                                            {touched.requestPayloadType && errors.requestPayloadType &&
                                                <div className="text-danger">{errors.requestPayloadType}</div>}
                                        </div>

                                        {/* Profile Type */}
                                        <div className="mb-3">
                                            <label className="form-label mb-2">{t('ProfileType')}</label>
                                            <FormikSelect
                                                name="profileTypeList"
                                                options={profileTypeOptions}
                                                isMulti
                                                isDisabled={loading}
                                                placeholder={`-- ${t('Select')} --`}
                                            />
                                            {touched.profileTypeList && errors.profileTypeList &&
                                                <div className="text-danger">{errors.profileTypeList}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card h-100">
                                    <div className="card-body">
                                        {/* Post Location */}
                                        <div className="mb-3">
                                            <label className="form-label">{t('PostLocation')}</label>
                                            <Field className="form-control" name="postLocation" 
                                                disabled={Number(values.serviceConsumingType) !== IServiceConsumingType.Passthrough}
                                                value={Number(values.serviceConsumingType) !== IServiceConsumingType.Passthrough ? "" : values.postLocation}
                                            />
                                            {touched.postLocation && errors.postLocation &&
                                                <div className="text-danger">{errors.postLocation}</div>}
                                        </div>
                                        {/* Access Token Url */}
                                        <div className="mb-3">
                                            <label className="form-label">{t('AccessTokenUrl')}</label>
                                            <Field className="form-control" name="accessTokenUrl"
                                                disabled={!(Number(values.serviceConsumingType) === IServiceConsumingType.Passthrough && (Number(values.authenticationType) === IAuthenticationType.Jwt))}
                                                value={(Number(values.serviceConsumingType) === IServiceConsumingType.Passthrough && Number(values.authenticationType) === IAuthenticationType.Jwt) ? values.accessTokenUrl : ""}
                                            />
                                            {touched.accessTokenUrl && errors.accessTokenUrl &&
                                                <div className="text-danger">{errors.accessTokenUrl}</div>}
                                        </div>
                                        {/* Retry Interval */}
                                        <div className="mb-3">
                                            <label className="form-label">{t('RetryInterval')}</label>
                                            <Field type="number" className="form-control" name="retryInterval"
                                                disabled={Number(values.operationType) !== IOperationType.Async}
                                                value={Number(values.operationType) !== IOperationType.Async ? 0 : values.retryInterval}
                                            />
                                            {touched.retryInterval && errors.retryInterval &&
                                                <div className="text-danger">{errors.retryInterval}</div>}
                                        </div>
                                        {/* Enabled */}
                                        <div className="mb-3">
                                            <label>
                                                <Field 
                                                    disabled={!(
                                                        (canApproveTransactions && (!isEdit || (isEdit && initialValues.isApproved))) //Approved condition
                                                    )} 
                                                    className="form-check-input me-2" type="checkbox"
                                                    name="enabled"/>
                                                {t('Enabled')}
                                            </label>
                                            {touched.enabled && errors.enabled &&
                                                <div className="text-danger">{errors.enabled}</div>}
                                        </div>
                                        {/* Spoke Submission Only */}
                                        <div className="mb-3">
                                            <label>
                                                <Field
                                                    className="form-check-input me-2" type="checkbox"
                                                    name="spokeSubmissionOnly"/>
                                                {t('SpokeSubmissionOnly')}
                                            </label>
                                            <div className="form-text">{t('SpokeSubmissionOnlyHelp')}</div>
                                            {touched.spokeSubmissionOnly && errors.spokeSubmissionOnly &&
                                                <div className="text-danger">{errors.spokeSubmissionOnly}</div>}
                                        </div>
                                        {/* Operation Type */}
                                        <div className="mb-3">
                                            <label className="form-label">{t('OperationType')}</label>
                                            <FormikSelect
                                                name="operationType"
                                                options={operationTypeOptions}
                                                isClearable
                                                clearValue={IOperationType.Unknown}
                                                isDisabled={loading}
                                                placeholder={`-- ${t('Select')} --`}
                                            />
                                            {touched.operationType && errors.operationType &&
                                                <div className="text-danger">{errors.operationType}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <BackButton
                                goTo={`/services/${serviceid}/transactions/list`}/>

                            <button type="submit" className="btn ms-3 gg-btn-submit">
                                {isEdit ? t('Update') : t('Create')}
                            </button>{canApproveTransactions}
                            {!service.isDeleted && !initialValues.isDeleted && canApproveTransactions && isEdit && !initialValues.isApproved ?
                                <button type="button"
                                        disabled={!canApproveTransactions}
                                        className={`btn ms-3 gg-btn-black`}
                                        onClick={() => {handleApprove(initialValues.id, initialValues.routeLabel)}}>
                                    {t('Approve')}
                                </button> : <></>
                            }
                        </div>

                    </Form>
                )}

            </Formik>

            <ConfirmModal
                show={showApproveModal}
                title={t('Approve')}
                message={t('ItemApproveConfirmation', {selectedName: selectedName} )}
                onConfirm={handleConfirmApprove}
                onCancel={handleCancelApprove}
                buttonClass={'warning'}
                loading={loading}
            />

        </div>
    );
}
export default TransactionForm;
